import { supabase } from '@/integrations/supabase/client';

/**
 * Compute a before/after diff for specified fields between two objects.
 * Only includes fields that actually changed.
 * 
 * Example output: { stage: { from: "prospect", to: "funded" }, sale_price: { from: 10000, to: 12000 } }
 */
export function computeChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  fields: string[]
): Record<string, { from: unknown; to: unknown }> {
  const changes: Record<string, { from: unknown; to: unknown }> = {};

  for (const field of fields) {
    const oldVal = before[field] ?? null;
    const newVal = after[field] ?? null;

    // Normalize comparison: treat empty strings as null
    const normOld = oldVal === '' ? null : oldVal;
    const normNew = newVal === '' ? null : newVal;

    if (JSON.stringify(normOld) !== JSON.stringify(normNew)) {
      changes[field] = { from: normOld, to: normNew };
    }
  }

  return changes;
}

/**
 * Log an action to the universal audit_log table.
 * Fire-and-forget — errors are logged to console but don't block the caller.
 * 
 * If performedBy is not provided, it will attempt to resolve from the current session.
 */
export async function logAudit(params: {
  entityType: string;
  entityId?: string | null;
  action: string;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  performedBy?: string | null;
}): Promise<void> {
  // Auto-resolve performer if not provided
  let performedBy = params.performedBy;
  if (!performedBy) {
    try {
      const { data } = await supabase.auth.getUser();
      performedBy = data?.user?.id || null;
    } catch {
      performedBy = null;
    }
  }

  const { error } = await supabase.from('audit_log').insert({
    entity_type: params.entityType,
    entity_id: params.entityId || null,
    action: params.action,
    changes: params.changes || {},
    metadata: params.metadata || {},
    performed_by: performedBy,
  } as any);

  if (error) {
    console.error('Audit log failed:', error);
  }
}
